import { useMemo, useState } from "react";
import { C, S } from "../customers/theme";
import ProductPicker from "../products/ProductPicker";
import { computeTotals, SO_STATUSES, CURRENCIES, RESERVING_STATUSES } from "../lib/salesOrders";
import { resolvePrice, resolvePrices } from "../lib/pricing";
import { todayISO } from "../lib/dates";

const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const num = (n) => Number(n) || 0;

let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder }) {
  const id = useMemo(() => `so-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      {options ? (
        <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input id={id} style={S.input} type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

const blankLine = () => ({ product_id: null, sku: "", description: "", quantity: 1, unit_price: 0, discount_percent: 0, vat_rate: 23 });

/**
 * Create/Edit Sales Order. Receives customers, products and a stock map (product_id →
 * getStockMap()'s row shape: current_stock, reserved_stock, available_stock,
 * stock_status) as props — never touches Supabase directly (except pricing.js, the
 * resolver, which auto-fills line prices). Shows live stock availability +
 * reservation/oversell warnings per product line.
 */
export default function SalesOrderForm({ initial, customers = [], products = [], stockMap = {}, onCancel, onSave }) {
  const seed = initial || {};
  const isEdit = !!seed.id;
  const [f, setF] = useState({
    customer_id: seed.customer_id || "",
    customer_name: seed.customer_name || "",
    order_date: seed.order_date || todayISO(),
    requested_delivery_date: seed.requested_delivery_date || "",
    salesperson: seed.salesperson || "",
    reference: seed.reference || "",
    status: seed.status || "draft",
    preferred_currency: seed.preferred_currency || "EUR",
    notes: seed.notes || "",
  });
  const initLines = (seed.sales_order_lines && seed.sales_order_lines.length) ? seed.sales_order_lines.map((l) => ({ ...l })) : [blankLine()];
  const [lines, setLines] = useState(initLines);
  // Transient per-line resolver meta (NOT persisted; parallel to `lines` so the save
  // payload is byte-identical). { sourceLabel, matchedMinQty, lastAuto }.
  const [meta, setMeta] = useState(() => initLines.map(() => ({})));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const totals = computeTotals(lines);
  const cur = f.preferred_currency === "GBP" ? "£" : f.preferred_currency === "USD" ? "$" : "€";
  const reserves = RESERVING_STATUSES.includes(f.status);

  // ── Resolver wiring (auto-fill; user can always override) ────────────────────
  const notOverridden = (l, m) => !!(l.product_id && m && m.lastAuto != null && Number(l.unit_price) === Number(m.lastAuto));
  // A price the system could not resolve must NEVER look like a price it did resolve.
  // On failure: clear the line's price (don't leave whatever was there standing in for
  // a price that was never actually recomputed), mark it unpriced so the badge below
  // warns instead of showing a stale/absent source, and surface the failure in the same
  // error banner the form already uses for save failures.
  const applyResolve = async (i, productId, qty) => {
    if (!productId) return;
    try {
      const r = await resolvePrice(f.customer_id || null, productId, Number(qty) || 1);   // base unit this WP
      setLines((p) => p.map((l, j) => (j === i ? { ...l, unit_price: r.unitPrice } : l)));
      setMeta((p) => p.map((m, j) => (j === i ? { sourceLabel: r.sourceLabel, matchedMinQty: r.matchedMinQty, lastAuto: r.unitPrice } : m)));
    } catch {
      setLines((p) => p.map((l, j) => (j === i ? { ...l, unit_price: 0 } : l)));
      setMeta((p) => p.map((m, j) => (j === i ? { sourceLabel: null, matchedMinQty: null, lastAuto: null, unpriced: true } : m)));
      setError("Couldn't fetch the price for this line. Check your connection and try again.");
    }
  };

  const onCustomer = async (id) => {
    const cu = customers.find((x) => x.id === id);
    setF((p) => ({ ...p, customer_id: id || "", customer_name: cu?.company_name || "", preferred_currency: cu?.preferred_currency || p.preferred_currency }));
    const todo = lines.map((l, i) => ({ l, i })).filter(({ l, i }) => notOverridden(l, meta[i]));
    if (!todo.length) return;
    try {
      const results = await resolvePrices(id || null, todo.map(({ l }) => ({ productId: l.product_id, qty: Number(l.quantity) || 1 })));
      const byIndex = Object.fromEntries(todo.map((t, k) => [t.i, results[k]]));
      setLines((p) => p.map((l, j) => (byIndex[j] ? { ...l, unit_price: byIndex[j].unitPrice } : l)));
      setMeta((p) => p.map((m, j) => (byIndex[j] ? { sourceLabel: byIndex[j].sourceLabel, matchedMinQty: byIndex[j].matchedMinQty, lastAuto: byIndex[j].unitPrice } : m)));
    } catch {
      // Same rule as applyResolve — clear every line this call was about to reprice,
      // not leave their old prices standing in for new ones that were never resolved.
      const todoIndexes = new Set(todo.map((t) => t.i));
      setLines((p) => p.map((l, j) => (todoIndexes.has(j) ? { ...l, unit_price: 0 } : l)));
      setMeta((p) => p.map((m, j) => (todoIndexes.has(j) ? { sourceLabel: null, matchedMinQty: null, lastAuto: null, unpriced: true } : m)));
      setError("Couldn't fetch prices for this customer. Check your connection and try again.");
    }
  };

  const updLine = (i, patch) => setLines((p) => p.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const addLine = () => { setLines((p) => [...p, blankLine()]); setMeta((p) => [...p, {}]); };
  const delLine = (i) => { setLines((p) => p.filter((_, j) => j !== i)); setMeta((p) => p.filter((_, j) => j !== i)); };
  const onPickProduct = (i, prod) => {
    const base = prod.wholesale_price ?? prod.retail_price ?? prod.unit_price ?? 0;
    updLine(i, { product_id: prod.id, sku: prod.sku || "", description: prod.name, unit_price: +base, vat_rate: prod.vat_rate ?? 23 });
    applyResolve(i, prod.id, lines[i]?.quantity ?? 1);
  };
  const onQty = (i, val) => {
    const qty = +val;
    const l = lines[i];
    const reprice = notOverridden(l, meta[i]);   // capture BEFORE the qty write
    updLine(i, { quantity: qty });
    if (reprice) applyResolve(i, l.product_id, qty);
  };

  // per-line stock preview: how much could be reserved right now, accounting for
  // earlier lines in this same order that target the same product.
  const consumed = {};
  const lineStock = lines.map((l) => {
    if (!l.product_id || !stockMap[l.product_id]) return null;
    const s = stockMap[l.product_id];
    const used = consumed[l.product_id] || 0;
    const available = Math.max(0, num(s.available_stock) - used);
    const willReserve = reserves ? Math.min(num(l.quantity), available) : 0;
    consumed[l.product_id] = used + willReserve;
    return { available: num(s.available_stock), current: num(s.current_stock), willReserve, short: reserves && num(l.quantity) > available, status: s.stock_status };
  });
  const anyShort = lineStock.some((s) => s && s.short);

  const submit = async () => {
    setError("");
    if (!f.customer_id) { setError("Please select a customer."); return; }
    setBusy(true);
    try {
      await onSave({ ...f, customer_id: f.customer_id || null, lines: lines.map(({ id, line_total, position, reserved_quantity, allocated_quantity, ...rest }) => rest) });
    } catch (e) { setError(e.message || "Could not save sales order."); setBusy(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{ ...S.modalBox, width: 880 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? "Edit Sales Order" : "New Sales Order"}
            {seed.so_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8, fontFamily: "monospace" }}>{seed.so_number}</span>}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>

        <div style={S.grid2}>
          <Field label="Customer *" value={f.customer_id} onChange={onCustomer}
            options={[{ value: "", label: customers.length ? "Select customer…" : "No customers yet" }, ...customers.map((cu) => ({ value: cu.id, label: `${cu.company_name}${cu.customer_number ? ` (${cu.customer_number})` : ""}` }))]} />
          <Field label="Status" value={f.status} onChange={set("status")} options={SO_STATUSES} />
          <Field label="Order Date" type="date" value={f.order_date} onChange={set("order_date")} />
          <Field label="Requested Delivery" type="date" value={f.requested_delivery_date} onChange={set("requested_delivery_date")} />
          <Field label="Salesperson" value={f.salesperson} onChange={set("salesperson")} />
          <Field label="Reference" value={f.reference} onChange={set("reference")} placeholder="Customer PO / ref" />
        </div>

        <div style={{ ...S.sectionTitle, display: "flex", justifyContent: "space-between" }}>
          <span>Line Items</span>
          <span style={{ fontSize: 11, fontWeight: 500, color: reserves ? C.textMuted : C.textDim }}>
            {reserves ? "This order reserves stock" : "This status does not reserve stock"}
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 64px 90px 56px 56px 84px 28px", gap: 6, marginBottom: 6, fontSize: 11, fontWeight: 700, color: C.textDim }}>
          {["Description", "SKU", "Qty", "Unit Price", "Disc%", "VAT%", "Line Total", ""].map((h) => <div key={h}>{h}</div>)}
        </div>
        {lines.map((l, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <ProductPicker products={products} style={{ marginBottom: 5, width: "100%" }} onSelect={(p) => onPickProduct(i, p)} />
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 64px 90px 56px 56px 84px 28px", gap: 6, alignItems: "center" }}>
              <input style={S.input} value={l.description} placeholder="Description" onChange={(e) => updLine(i, { description: e.target.value })} />
              <input style={S.input} value={l.sku || ""} placeholder="SKU" onChange={(e) => updLine(i, { sku: e.target.value })} />
              <input style={S.input} type="number" value={l.quantity} onChange={(e) => onQty(i, e.target.value)} />
              <input style={S.input} type="number" step="0.01" value={l.unit_price} onChange={(e) => updLine(i, { unit_price: +e.target.value })} />
              <input style={S.input} type="number" value={l.discount_percent} onChange={(e) => updLine(i, { discount_percent: +e.target.value })} />
              <select style={S.input} value={l.vat_rate} onChange={(e) => updLine(i, { vat_rate: +e.target.value })}>
                {[0, 9, 13.5, 23].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <input style={{ ...S.input, background: C.surface, color: C.textMuted }} value={money(totals.lines[i]?.line_total, cur)} readOnly tabIndex={-1} />
              <button style={{ ...S.btn("ghost"), padding: "4px 6px" }} aria-label="Remove line" onClick={() => delLine(i)}>✕</button>
            </div>
            {meta[i]?.sourceLabel && (
              <div style={{ fontSize: 10.5, color: C.textDim, marginTop: 3 }}
                title={meta[i].matchedMinQty != null ? `Matched min qty ${meta[i].matchedMinQty}` : "No quantity break applied"}>
                price source: {meta[i].sourceLabel}{meta[i].matchedMinQty != null ? ` · min ${meta[i].matchedMinQty}` : ""}
              </div>
            )}
            {meta[i]?.unpriced && (
              <div style={{ fontSize: 10.5, color: C.danger, marginTop: 3, fontWeight: 600 }}>
                ⚠ price could not be resolved — re-select the product or enter a price manually
              </div>
            )}
            {lineStock[i] && (
              <div style={{ fontSize: 11, marginTop: 3, color: lineStock[i].short ? C.danger : C.textMuted }}>
                {lineStock[i].short
                  ? `⚠ Only ${lineStock[i].available} available — ${lineStock[i].willReserve} will be reserved, ${num(l.quantity) - lineStock[i].available} short`
                  : reserves
                    ? `✓ ${lineStock[i].available} available · reserving ${lineStock[i].willReserve}`
                    : `${lineStock[i].available} available (no reservation in this status)`}
              </div>
            )}
          </div>
        ))}
        <button style={S.btn("secondary")} onClick={addLine}>+ Add Line</button>

        {anyShort && (
          <div role="alert" style={{ marginTop: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600 }}>
            ⚠ Some lines exceed available stock. Only what's in stock will be reserved (no overselling); the shortfall stays unreserved until more stock is received.
          </div>
        )}

        <div style={{ background: C.surface2, borderRadius: 8, padding: 14, margin: "16px 0", maxWidth: 320, marginLeft: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Subtotal</span><strong>{money(totals.subtotal, cur)}</strong></div>
          {totals.total_discount > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Discount</span><strong>−{money(totals.total_discount, cur)}</strong></div>}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>VAT</span><strong>{money(totals.total_vat, cur)}</strong></div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Total</span><span>{money(totals.total_amount, cur)}</span></div>
        </div>

        <div style={S.grid2}>
          <Field label="Currency" value={f.preferred_currency} onChange={set("preferred_currency")} options={CURRENCIES} />
          <div><label style={S.label}>Notes</label><textarea style={{ ...S.input, height: 44 }} value={f.notes} onChange={(e) => set("notes")(e.target.value)} /></div>
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 14 }} role="alert">{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Create Sales Order"}
          </button>
        </div>
      </div>
    </div>
  );
}
